/**
 * F 487 — keep a sealed block sealed when the editor saves.
 *
 * THE BUG THIS FIXES, MEASURED
 * ---------------------------
 * The seal works: `encryptBlock` replaces the block in the page's `Block[]`
 * with a stub whose `attributes.encrypted` is `true`, whose
 * `attributes.anchor` is the public tag, and whose `anchors` array carries
 * the `Anchor` (kind `hint`, source `user`). That is what the F 429 engine
 * suite proves, and it is correct.
 *
 * But the page's content does not live only in that array. It also lives in
 * the EDITOR, and the editor autosaves:
 *
 *     const blocks = editor.document;
 *     savePageContent(effectivePageId, blocks);
 *
 * So the question is what `editor.document` holds after a seal. And the
 * answer, measured on production against the GRAPH (not the localStorage
 * mirror), is this:
 *
 *     page-prd (sealed through the drag-handle menu):
 *         enc=True  anchor='f487-race-anchor'  text='🔒 [encrypted block]'
 *         anchors: [{"id":"...:anchor:1792960250906","text":"f487-race-anchor",
 *                    "kind":"hint","source":"user",...}]
 *
 *     page-1792960335793-xxjm40zlf (sealed through the SAME code path):
 *         enc=None anchor=None text='🔒 [encrypted block]'
 *
 * The `🔒 [encrypted block]` TEXT survives, because that is inline content
 * and BlockNote's schema carries inline content. The `encrypted` flag, the
 * `anchor` and the `anchors` array do NOT survive — because BlockNote's
 * paragraph block spec has nowhere to put them. `blockToNode` drops what the
 * schema does not declare, and `nodeToBlock` cannot invent it back.
 *
 * So the editor's autosave silently REPLACED the sealed block with a
 * plaintext-shaped block that merely LOOKS sealed. The operator's step 4 —
 * "only then see the semantic tag created from the content" — was broken:
 * the tag was gone.
 *
 * THE RULE
 * --------
 * A block that is SEALED IN STORAGE stays sealed in storage. The editor's
 * copy of that block cannot un-seal it, cannot re-encrypt it (it has no
 * plaintext — that is the whole point), and cannot change its anchor
 * without going through the seal path.
 *
 * So the merge is: take the editor's document, and for every block whose id
 * is sealed in the stored content, put the STORED block back. The stored
 * block is the authority. The editor's version is a rendering of it.
 *
 * WHAT THIS DOES NOT DO
 * ---------------------
 * It does not make the editor able to carry the metadata. That would need a
 * custom block spec, which is a schema change and outside this dispatch's
 * bounds. It does not stop the operator from DECRYPTING: the decrypt path
 * writes a plaintext block to storage, so the stored block is no longer
 * sealed, and the merge leaves the editor's version alone. The test below
 * proves that.
 */

/** The shape of the fields a sealed block carries. */
interface SealedShape {
  attributes?: { encrypted?: unknown };
  anchors?: unknown;
}

/** True when the stored block is a sealed stub. */
export function isSealedBlock(block: unknown): boolean {
  if (!block || typeof block !== 'object') return false;
  const attrs = (block as SealedShape).attributes;
  return !!attrs && attrs.encrypted === true;
}

/**
 * Merge the editor's document with the stored page content, so that a
 * sealed block keeps the stored form.
 *
 * The EDITOR's document is the base — it is what the operator is looking
 * at, and it carries every edit. The STORED content is consulted only for
 * the blocks that are sealed, because those are the ones the editor's schema
 * cannot represent faithfully.
 *
 * Blocks are matched BY ID. A block that is in the editor but not in
 * storage is a new block and is kept. A block that is in storage but not in
 * the editor was removed by the operator — the editor wins, because the
 * operator's deletion is a real intent.
 *
 * Returns the editor's document unchanged when the stored content holds no
 * sealed block, so the common path costs one pass and no allocation.
 */
export function mergeSealedBlocks(
  editorBlocks: any[],
  storedBlocks: any[] | null | undefined,
): any[] {
  if (!Array.isArray(editorBlocks)) return editorBlocks;
  if (!Array.isArray(storedBlocks) || storedBlocks.length === 0) return editorBlocks;

  // Index the stored sealed blocks by id. Only sealed ones matter.
  const sealedById = new Map<string, any>();
  for (const block of storedBlocks) {
    if (!isSealedBlock(block)) continue;
    const id = (block as { id?: unknown })?.id;
    if (typeof id === 'string' && id) sealedById.set(id, block);
  }
  if (sealedById.size === 0) return editorBlocks;

  let changed = false;
  const merged = editorBlocks.map((block) => {
    const id = (block as { id?: unknown })?.id;
    if (typeof id !== 'string' || !id) return block;
    const stored = sealedById.get(id);
    if (!stored) return block;
    // The stored block is the authority for a sealed block. If the editor
    // already carries the same sealed form, there is nothing to change.
    if (isSealedBlock(block)) return block;
    changed = true;
    return stored;
  });

  return changed ? merged : editorBlocks;
}
